import { cn } from "@/shared/lib/cn";
import { useUIStore } from "@/shared/stores/ui-store";
import { Badge } from "@/shared/ui/badge";
import { Button } from "@/shared/ui/button";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "@/shared/ui/dropdown-menu";
import { Tooltip, TooltipContent, TooltipTrigger } from "@/shared/ui/tooltip";
import { Notifications } from "@/widgets/notifications/ui/Notifications";
import { useAuth0 } from "@auth0/auth0-react";
import { Activity } from "iconsax-react";
import { Bell, LogOut } from "lucide-react";
import { useState } from "react";

interface HeaderProps {
  showActivityLog: boolean;
  onToggleActivityLog: () => void;
}

export function Header({ showActivityLog, onToggleActivityLog }: HeaderProps) {
  const {
    notifications,
    isWebSocketEnabled,
    isWebSocketConnected,
    setWebSocketEnabled,
    markNotificationsAsRead,
  } = useUIStore();
  const [notificationsOpen, setNotificationsOpen] = useState(false);
  const { logout } = useAuth0();

  const unreadCount = notifications.filter((n) => !n.read).length;

  const handleLogout = () => {
    const auth0Keys = Object.keys(localStorage).filter((key) => key.startsWith("@@auth0spajs@@"));
    auth0Keys.forEach((key) => localStorage.removeItem(key));
    localStorage.removeItem("auth_token");

    sessionStorage.clear();

    logout({
      logoutParams: {
        federated: false,
      },
    });

    setTimeout(() => {
      window.location.replace("/login");
    }, 100);
  };

  return (
    <header className="h-16 border-b bg-gray-700 text-white">
      <div className="flex h-full items-center justify-between px-4">
        <div className="flex items-center gap-4">
          <button
            onClick={() => setWebSocketEnabled(!isWebSocketEnabled)}
            className="flex cursor-pointer items-center gap-2 transition-opacity hover:opacity-70"
          >
            <div
              className={cn(
                "h-2 w-2 rounded-full",
                isWebSocketEnabled && isWebSocketConnected ? "bg-green-500" : "bg-red-500"
              )}
            />
            <span className="text-sm opacity-80">
              {isWebSocketEnabled && isWebSocketConnected
                ? "Live"
                : isWebSocketEnabled
                  ? "Connecting..."
                  : "Not Connected"}
            </span>
          </button>
        </div>

        <div className="flex items-center gap-1">
          <Tooltip>
            <TooltipTrigger asChild>
              <Button
                variant="ghost"
                size="icon"
                onClick={onToggleActivityLog}
                className={cn("group !p-2 text-white", showActivityLog && "bg-blue-700")}
              >
                <Activity
                  size={24}
                  variant="Linear"
                  color="currentColor"
                  className={cn(
                    "h-6 w-6 transition-colors group-hover:text-gray-600",
                    showActivityLog && "text-white"
                  )}
                />
              </Button>
            </TooltipTrigger>
            <TooltipContent>
              {showActivityLog ? "Hide Activity Log" : "Show Activity Log"}
            </TooltipContent>
          </Tooltip>

          <DropdownMenu
            open={notificationsOpen}
            onOpenChange={(open) => {
              setNotificationsOpen(open);
              if (open) {
                markNotificationsAsRead();
              }
            }}
          >
            <DropdownMenuTrigger asChild>
              <Button
                variant="ghost"
                size="icon"
                className="group !p-2 text-white data-[state=open]:bg-blue-700"
              >
                <span className="relative inline-block">
                  <Bell className="h-6 w-6 transition-colors group-hover:text-gray-600 group-data-[state=open]:text-white" />
                  {unreadCount > 0 && (
                    <Badge className="absolute -right-1 -top-1 flex h-3 w-3 items-center justify-center rounded-full bg-red-600 p-0 text-[10px] leading-none text-white">
                      {unreadCount}
                    </Badge>
                  )}
                </span>
              </Button>
            </DropdownMenuTrigger>
            <DropdownMenuContent
              align="end"
              className="w-80 p-0"
              onCloseAutoFocus={(e) => e.preventDefault()}
            >
              <Notifications onClose={() => setNotificationsOpen(false)} />
            </DropdownMenuContent>
          </DropdownMenu>

          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <Button
                variant="ghost"
                size="icon"
                className="group !p-2 text-white data-[state=open]:bg-blue-700"
              >
                <LogOut className="h-6 w-6 transition-colors group-hover:text-gray-600 group-data-[state=open]:text-white" />
              </Button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end">
              <DropdownMenuItem onClick={handleLogout}>
                <LogOut className="mr-2 h-4 w-4" />
                Logout
              </DropdownMenuItem>
            </DropdownMenuContent>
          </DropdownMenu>
        </div>
      </div>
    </header>
  );
}
